import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Directionality } from '@angular/cdk/bidi';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { type Observable, forkJoin, map } from 'rxjs';
import { switchLanguage } from '@core/i18n/i18n.config';
import { PartnerLinks } from './partner-links';
import { LOCAL_STORAGE } from '@core/storage/storage.tokens';
import { LOCAL_KEYS } from '@core/storage/storage.keys';
import { PartnerAccountApiService } from '@core/api/partner-portal/partner-account-api.service';
import type {
  Partner,
  PartnerAuthResponse,
  PartnerLanguage,
  PartnerSubscription,
  PartnerUser,
} from '@typings/app/partner-portal.types';

export type PartnerLogoutReason = 'expired' | 'suspended';

/**
 * The Partner Portal session: the partner user's token, profile and partner (with subscription).
 * Fully separate from the employer session — it never reads or clears employer keys.
 */
@Injectable({ providedIn: 'root' })
export class PartnerSessionService {
  private readonly storage = inject(LOCAL_STORAGE);
  private readonly router = inject(Router);
  private readonly links = inject(PartnerLinks);
  private readonly accountApi = inject(PartnerAccountApiService);
  private readonly translate = inject(TranslateService);
  private readonly doc = inject(DOCUMENT);
  private readonly directionality = inject(Directionality);

  private readonly _token = signal<string | null>(this.storage.get(LOCAL_KEYS.partnerToken));
  private readonly _user = signal<PartnerUser | null>(this.storage.get(LOCAL_KEYS.partnerUser));
  private readonly _partner = signal<Partner | null>(null);
  private readonly _subscription = signal<PartnerSubscription | null>(null);
  private readonly logoutHooks: (() => void)[] = [];

  readonly token = this._token.asReadonly();
  readonly user = this._user.asReadonly();
  readonly partner = this._partner.asReadonly();
  /** The partner's current subscription; `null` when it has none (or before `refresh()`). */
  readonly subscription = this._subscription.asReadonly();
  readonly isAuthenticated = computed(() => !!this._token());
  readonly role = computed(() => this._user()?.role ?? null);
  readonly isSuperAdmin = computed(() => this.role() === 'super_admin');

  /**
   * Store a fresh session (sign in / accept invitation) and switch to the user's language.
   * The partner is loaded by the shell's `refresh()` right after navigation.
   */
  startSession(res: PartnerAuthResponse): void {
    // A new session may belong to a different partner: drop the previous one's caches.
    this.logoutHooks.forEach((hook) => hook());
    this._partner.set(null);
    this._subscription.set(null);
    this.storage.set(LOCAL_KEYS.partnerToken, res.access_token);
    this._token.set(res.access_token);
    this.updateUser(res.partner_user);
    this.applyLanguage(res.partner_user.language);
  }

  /** Re-read the user (their role may have changed) and the partner/subscription. */
  refresh(): Observable<void> {
    return forkJoin([this.accountApi.getMe(), this.accountApi.getPartner()]).pipe(
      map(([me, { partner, current_subscription }]) => {
        this.updateUser(me.partner_user);
        this._partner.set(partner);
        this._subscription.set(current_subscription ?? null);
      }),
    );
  }

  updateUser(user: PartnerUser): void {
    this.storage.set(LOCAL_KEYS.partnerUser, user);
    this._user.set(user);
  }

  applyLanguage(lang: PartnerLanguage): void {
    switchLanguage(this.translate, this.doc, this.storage, this.directionality, lang);
  }

  /** Switch the UI language now and save it as the account's language (best effort). */
  changeLanguage(lang: PartnerLanguage): void {
    this.applyLanguage(lang);
    if (!this.isAuthenticated()) return;
    this.accountApi.updateMe({ language: lang }).subscribe({
      next: ({ partner_user }) => this.updateUser(partner_user),
      error: () => undefined,
    });
  }

  /** Register cleanup for partner-scoped caches (e.g. the company directory); runs on logout and new sessions. */
  onLogout(hook: () => void): void {
    this.logoutHooks.push(hook);
  }

  logout(reason?: PartnerLogoutReason): void {
    this.storage.remove(LOCAL_KEYS.partnerToken);
    this.storage.remove(LOCAL_KEYS.partnerUser);
    this._token.set(null);
    this._user.set(null);
    this._partner.set(null);
    this._subscription.set(null);
    this.logoutHooks.forEach((hook) => hook());
    void this.router.navigate([this.links.to('login')], reason === 'suspended' ? { queryParams: { reason } } : {});
  }
}
